/**
 * The creator's ad offers in the studio's "Ad offers" dock (ompportal plan creator-ad-approval-card, WP5b).
 *
 * The studio has no login of its own (it holds a stream key, and a stream key must never approve an ad, D7). Inside
 * the portal's creator dashboard the portal shows it the offers and makes its decisions (see `embedBridge.ts`):
 *   portal → studio: `{type:'omp:ad-offer', offer, logo?, preview?}` and `{type:'omp:ad-offer-resolved', offer_id,
 *   state, decided_surface}`; studio → portal: `{type:'omp:ad-offer-decide', offer_id, decision}`, nothing more.
 * Standalone, nothing can be answered here: the dock is read-only and points at the portal.
 *
 * Module-level store, like the activity feed's, so the offers survive the dock being closed and reopened.
 */
import { createStore } from 'zustand/vanilla';

export type OfferState = 'pending' | 'accepted' | 'rejected' | 'expired' | 'withdrawn' | 'unknown';
export type OfferDecision = 'accept' | 'reject';
export type OfferSurface = 'portal' | 'studio' | 'omp_tools';

/** The backend's view of an offer (ompbackend `src/services/ad-offers/contract.ts`, `OfferView`). */
export interface OfferView {
  offer_id: string;
  state: OfferState;
  brand: { name: string; logo_url: string | null };
  net_cents: number;
  gross_cents: number;
  platform_cents: number;
  share_percent: number;
  currency: string;
  duration_ms: number;
  preview_url: string | null;
  placement: string;
  renderer: string;
  when_text: string;
  categories: string[];
  branded_content: boolean;
  offered_at: string;
  decide_until: string;
  /** The server's clock as the portal reckons it at the time it posted the offer. */
  server_now: string;
  attempt: number | null;
  max_attempts: number | null;
}

export interface OfferResolved {
  offer_id: string;
  state: OfferState;
  decided_surface: OfferSurface | null;
}

export interface AdOfferDecideMessage {
  type: 'omp:ad-offer-decide';
  offer_id: string;
  decision: OfferDecision;
}

export const NOTICE_MS = 4_000;
export const DECIDE_WINDOW_MS = 60_000;

export type CardStatus =
  | { kind: 'open' }
  | { kind: 'deciding'; decision: OfferDecision }
  | { kind: 'expired' }
  | { kind: 'resolved'; state: OfferState; surface: OfferSurface | null };

export interface StudioOfferCard {
  offer: OfferView;
  /** Server clock minus local clock. */
  skewMs: number;
  status: CardStatus;
  logoUrl: string | null;
  previewUrl: string | null;
  closedAt: number | null;
}

const STATES = new Set<OfferState>(['pending', 'accepted', 'rejected', 'expired', 'withdrawn', 'unknown']);
const SURFACES = new Set<OfferSurface>(['portal', 'studio', 'omp_tools']);
const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
/** Same names and status lines as omp-tools' native card and the portal card. */
const SURFACE_NAME: Record<OfferSurface, string> = { portal: 'Perck', studio: 'Studio', omp_tools: 'omp-tools' };

const isStr = (v: unknown): v is string => typeof v === 'string';
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isTime = (v: unknown): v is string => isStr(v) && Number.isFinite(Date.parse(v));
const isImage = (v: unknown): v is Blob => typeof Blob !== 'undefined' && v instanceof Blob && IMAGE_TYPES.has(v.type);

function parseOffer(v: unknown): OfferView | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const brand = o.brand as Record<string, unknown> | null | undefined;
  if (!isStr(o.offer_id) || !o.offer_id || o.offer_id.length > 128 || o.state !== 'pending') return null;
  if (!brand || typeof brand !== 'object' || !isStr(brand.name)) return null;
  if (![o.net_cents, o.gross_cents, o.platform_cents, o.share_percent, o.duration_ms].every(isNum)) return null;
  if (![o.currency, o.placement, o.renderer, o.when_text].every(isStr) || typeof o.branded_content !== 'boolean') return null;
  if (![o.offered_at, o.decide_until, o.server_now].every(isTime)) return null;
  if (!Array.isArray(o.categories) || !o.categories.every(isStr)) return null;
  return {
    offer_id: o.offer_id,
    state: 'pending',
    brand: { name: brand.name, logo_url: isStr(brand.logo_url) ? brand.logo_url : null },
    net_cents: o.net_cents as number,
    gross_cents: o.gross_cents as number,
    platform_cents: o.platform_cents as number,
    share_percent: o.share_percent as number,
    currency: o.currency as string,
    duration_ms: o.duration_ms as number,
    preview_url: isStr(o.preview_url) ? o.preview_url : null,
    placement: o.placement as string,
    renderer: o.renderer as string,
    when_text: o.when_text as string,
    categories: o.categories as string[],
    branded_content: o.branded_content,
    offered_at: o.offered_at as string,
    decide_until: o.decide_until as string,
    server_now: o.server_now as string,
    attempt: isNum(o.attempt) ? o.attempt : null,
    max_attempts: isNum(o.max_attempts) ? o.max_attempts : null,
  };
}

/** `omp:ad-offer` data: a pending offer and its images (Blobs of an allowed image type), or null. */
export function parseAdOfferMessage(data: unknown): { offer: OfferView; logo?: Blob; preview?: Blob } | null {
  if (!data || typeof data !== 'object') return null;
  const { type, offer, logo, preview } = data as Record<string, unknown>;
  if (type !== 'omp:ad-offer') return null;
  const parsed = parseOffer(offer);
  if (!parsed) return null;
  return { offer: parsed, ...(isImage(logo) ? { logo } : {}), ...(isImage(preview) ? { preview } : {}) };
}

/** `omp:ad-offer-resolved` data, or null. */
export function parseAdOfferResolved(data: unknown): OfferResolved | null {
  if (!data || typeof data !== 'object') return null;
  const { type, offer_id, state, decided_surface } = data as Record<string, unknown>;
  if (type !== 'omp:ad-offer-resolved' || !isStr(offer_id) || !STATES.has(state as OfferState)) return null;
  return { offer_id, state: state as OfferState, decided_surface: SURFACES.has(decided_surface as OfferSurface) ? (decided_surface as OfferSurface) : null };
}

/** The studio's answer: the offer and the decision, and nothing else (the portal adds who and where). */
export function adOfferDecideMessage(offerId: string, decision: OfferDecision): AdOfferDecideMessage {
  return { type: 'omp:ad-offer-decide', offer_id: offerId, decision };
}

export function remainingMs(card: StudioOfferCard, localNow: number): number {
  const until = Date.parse(card.offer.decide_until);
  return Number.isFinite(until) ? Math.max(0, until - (localNow + card.skewMs)) : 0;
}

export const secondsLeft = (ms: number): number => Math.ceil(ms / 1000);

export const isClosed = (card: StudioOfferCard): boolean => card.status.kind === 'expired' || card.status.kind === 'resolved';

export function canDecide(card: StudioOfferCard, localNow: number): boolean {
  return card.status.kind === 'open' && remainingMs(card, localNow) > 0;
}

const answered = (state: OfferState): string => (state === 'accepted' ? 'Aceptada' : state === 'rejected' ? 'Rechazada' : 'Ya respondida');

/**
 * The card's status line, the same words as the portal and omp-tools: "Respondé en N s" while open (with `localNow`),
 * "Enviando…", "Venció", "Aceptada" / "Rechazada" for the Studio's own answer, "Ya respondida en Perck (Aceptada)".
 */
export function statusText(card: StudioOfferCard, localNow?: number): string | null {
  const { status } = card;
  if (status.kind === 'open') return localNow === undefined ? null : `Respondé en ${secondsLeft(remainingMs(card, localNow))} s`;
  if (status.kind === 'deciding') return 'Enviando…';
  if (status.kind === 'expired') return 'Venció';
  if (status.surface === 'studio') return answered(status.state);
  const what = status.state === 'accepted' || status.state === 'rejected' ? ` (${answered(status.state)})` : '';
  return status.surface ? `Ya respondida en ${SURFACE_NAME[status.surface]}${what}` : `Ya respondida${what}`;
}

/** The countdown as omp-tools draws it: "0:42". */
export function clockText(ms: number): string {
  const s = secondsLeft(ms);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** "La marca paga $ 2,50 · Comisión 50% ($ 1,25)". */
export function detailText(offer: OfferView): string {
  return `La marca paga ${formatMoney(offer.gross_cents, offer.currency)} · Comisión ${offer.share_percent}% (${formatMoney(offer.platform_cents, offer.currency)})`;
}

/** "Duración 15 s · Tercio inferior". */
export function whatText(offer: OfferView): string {
  const where = placementName(offer.placement, offer.renderer);
  return where ? `Duración ${formatOfferDuration(offer.duration_ms)} · ${where}` : `Duración ${formatOfferDuration(offer.duration_ms)}`;
}

export function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2).replace('.', ',')} ${currency}`;
  }
}

export function formatOfferDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const min = Math.floor(total / 60);
  const sec = total % 60;
  if (min === 0) return `${sec} s`;
  return sec === 0 ? `${min} min` : `${min} min ${sec} s`;
}

const PLACEMENT: Record<string, string> = {
  lower_third: 'Tercio inferior',
  fullscreen: 'Pantalla completa',
  full_screen: 'Pantalla completa',
  corner: 'Esquina',
  overlay: 'Superpuesto',
  mention: 'Mención',
};
const humanize = (code: string): string => {
  const words = code.replace(/[_-]+/g, ' ').trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : code;
};
export const placementLabel = (placement: string): string => PLACEMENT[placement] ?? humanize(placement);
export const categoryLabel = humanize;

/** An identifier, not a name: a UUID, a long hex/base32 run, or an id-prefixed token (plc_…, cat_…). Same rule as the portal. */
export function isOpaqueRef(value: string): boolean {
  return (
    /[0-9a-f]{8}[-_ ]?[0-9a-f]{4}[-_ ]?[0-9a-f]{4}[-_ ]?[0-9a-f]{4}[-_ ]?[0-9a-f]{12}/i.test(value) ||
    /^(plc|cat|ind|brd|adv|cmp|ad)[_-]/i.test(value) ||
    /(?=[a-z0-9]*\d)[a-z0-9]{16,}/i.test(value)
  );
}

/** The placement as words (known kinds, else the renderer's kind), or null: a placement id is never shown. */
export function placementName(placement: string, renderer = ''): string | null {
  if (PLACEMENT[placement]) return PLACEMENT[placement]!;
  const kind = /(?:^|\.)([a-z][a-z_]*[a-z])\.v\d+$/.exec(renderer)?.[1];
  if (kind && PLACEMENT[kind]) return PLACEMENT[kind]!;
  return placement && !isOpaqueRef(placement) ? humanize(placement) : null;
}

/** Categories as words (taxonomy ids such as "industry.<uuid>" are left out), then "Contenido de marca". */
export function offerLabels(offer: Pick<OfferView, 'categories' | 'branded_content'>): string[] {
  const words = offer.categories
    .map((category) => category.replace(/^(industry|category|iab)[.:]/i, ''))
    .filter((category) => category && !isOpaqueRef(category))
    .map(categoryLabel);
  return [...words, ...(offer.branded_content ? ['Contenido de marca'] : [])];
}

const objectUrl = (blob: Blob | undefined): string | null =>
  blob && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : null;
const release = (url: string | null): void => {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
};

export interface AdOfferState {
  cards: StudioOfferCard[];
  /** omp-tools runs and shows the offers natively (portal's `omp:ad-offer-presence`): the dock shows none. */
  native: boolean;
  setNative(native: boolean): void;
  /** Sends a decision to the portal; null when standalone (read-only). */
  decider: ((offerId: string, decision: OfferDecision) => void) | null;
  setDecider(decider: AdOfferState['decider']): void;
  /** An offer from the portal. True when it is new (worth a toast); an offer seen again reopens after a failed answer. */
  receive(message: { offer: OfferView; logo?: Blob; preview?: Blob }, localNow: number): boolean;
  resolve(resolved: OfferResolved, localNow: number): void;
  /** A click: sent when the card can be answered and the portal is there to send it. */
  decide(offerId: string, decision: OfferDecision, localNow: number): boolean;
  tick(localNow: number): void;
}

export const adOfferStore = createStore<AdOfferState>((set, get) => ({
  cards: [],
  native: false,
  setNative(native) {
    set({ native });
  },
  decider: null,
  setDecider(decider) {
    set({ decider });
  },
  receive({ offer, logo, preview }, localNow) {
    const skewMs = Date.parse(offer.server_now) - localNow;
    const known = get().cards.find((card) => card.offer.offer_id === offer.offer_id);
    if (known) {
      if (known.status.kind !== 'deciding') return false;
      set({ cards: get().cards.map((card) => (card === known ? { ...card, skewMs, status: { kind: 'open' } } : card)) });
      return false;
    }
    const card: StudioOfferCard = { offer, skewMs, status: { kind: 'open' }, logoUrl: objectUrl(logo), previewUrl: objectUrl(preview), closedAt: null };
    set({ cards: [...get().cards, card] });
    return true;
  },
  resolve(resolved, localNow) {
    if (resolved.state === 'pending') return;
    const status: CardStatus = resolved.state === 'expired' ? { kind: 'expired' } : { kind: 'resolved', state: resolved.state, surface: resolved.decided_surface };
    let changed = false;
    const cards = get().cards.map((card) => {
      if (card.offer.offer_id !== resolved.offer_id || isClosed(card)) return card;
      changed = true;
      return { ...card, status, closedAt: localNow };
    });
    if (changed) set({ cards });
  },
  decide(offerId, decision, localNow) {
    const { decider, cards } = get();
    const card = cards.find((c) => c.offer.offer_id === offerId);
    if (!decider || !card || !canDecide(card, localNow)) return false;
    set({ cards: cards.map((c) => (c === card ? { ...c, status: { kind: 'deciding', decision } } : c)) });
    decider(offerId, decision);
    return true;
  },
  tick(localNow) {
    let changed = false;
    const cards: StudioOfferCard[] = [];
    for (const card of get().cards) {
      if (card.closedAt !== null && localNow - card.closedAt >= NOTICE_MS) {
        release(card.logoUrl);
        release(card.previewUrl);
        changed = true;
        continue;
      }
      if (card.status.kind === 'open' && remainingMs(card, localNow) === 0) {
        changed = true;
        cards.push({ ...card, status: { kind: 'expired' }, closedAt: localNow });
        continue;
      }
      cards.push(card);
    }
    if (changed) set({ cards });
  },
}));
